import React, { useState } from 'react';
import { palettes, SmallTitle, Table, ColumnDefinition, RowDefinition } from '@indigo-ag/component-module';
import { ShareholderPartner, ShareholderCompany } from '@indigo-ag/schema';
import i18next from 'core/i18n';

import { ParticipationModal, ParticipationModalProps } from './components/ParticipationModal/ParticipationModal';
import {
  PhaseSection,
  PhaseSectionProps
} from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseSection';
import { brazil } from 'shared/utils';
import { IParticipationInfo } from 'shared';

export interface ParticipationProps
  extends Pick<PhaseSectionProps, 'disabled'>,
    Pick<ParticipationModalProps, 'updateCredit'> {
  participationInfo?: IParticipationInfo;
}

interface ParticipationSectionSummaryProps extends Pick<ParticipationProps, 'participationInfo'> {}
type ColumnIdPartnerShares = 'name' | 'cnpj' | 'position' | 'actions';
type ColumnIdCompanyShares = 'company' | 'companyShares' | 'cnpj';

const rowDefinitionShareHolders: RowDefinition<ShareholderPartner, ColumnIdPartnerShares> = {
  rowId: ({ row }) => row.CNPJ || ''
};
const rowDefinitionCompanyShare: RowDefinition<ShareholderCompany, ColumnIdCompanyShares> = {
  rowId: ({ row }) => row.CNPJ || ''
};

const configToRenderShareHolders: ColumnDefinition<ShareholderPartner, ColumnIdPartnerShares>[] = [
  {
    id: 'name',
    header: i18next.t('name'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.fullName} />
  },
  {
    id: 'cnpj',
    header: 'CNPJ',
    cell: ({ row, SimpleCell }) => {
      const isValidCnpj = row.CNPJ?.length === 14;
      return <SimpleCell label={isValidCnpj ? brazil.cnpjInputMask(`${row.CNPJ}`) : ''} />;
    }
  },
  {
    id: 'position',
    header: i18next.t('position'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.position} />
  },
  {
    id: 'actions',
    header: i18next.t('actions'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.participation}%`} />
  }
];

const configToRenderCompanyShares: ColumnDefinition<ShareholderCompany, ColumnIdCompanyShares>[] = [
  {
    id: 'company',
    header: i18next.t('company'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.name} />
  },
  {
    id: 'companyShares',
    header: i18next.t('companyShares'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${row.participation}%`} />
  },
  {
    id: 'cnpj',
    header: 'CNPJ',
    cell: ({ row, SimpleCell }) => {
      const isValidCnpj = row.CNPJ.length === 14;
      return <SimpleCell label={isValidCnpj ? brazil.cnpjInputMask(`${row.CNPJ}`) : ''} />;
    }
  }
];

export const ParticipationSectionSummary: React.FunctionComponent<ParticipationSectionSummaryProps> = ({
  participationInfo
}) => (
  <div className="width-100">
    {participationInfo?.shareholderPartner?.length ? (
      <>
        <SmallTitle style={{ color: palettes.neutral['800'] }}>{i18next.t('shareHolderPartner')}</SmallTitle>
        <Table
          label="Share holder partner"
          columnDefinition={configToRenderShareHolders}
          rowDefinition={rowDefinitionShareHolders}
          data={participationInfo.shareholderPartner}
        />
      </>
    ) : null}
    {participationInfo?.shareholderCompany?.length ? (
      <>
        <SmallTitle style={{ color: palettes.neutral['800'], paddingTop: 20 }}>{i18next.t('companyShares')}</SmallTitle>
        <Table
          label="Share holder company"
          columnDefinition={configToRenderCompanyShares}
          rowDefinition={rowDefinitionCompanyShare}
          data={participationInfo.shareholderCompany}
        />
      </>
    ) : null}
  </div>
);

export const Participation: React.FunctionComponent<ParticipationProps> = ({
  participationInfo,
  updateCredit,
  children,
  ...phaseSectionProps
}) => {
  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);

  const closeModal = (): void => setModalOpen(false);

  const openModal = (): void => setModalOpen(true);

  return (
    <>
      <PhaseSection
        {...phaseSectionProps}
        open={!!participationInfo?.shareholderCompany || !!participationInfo?.shareholderPartner}
        onClickEdit={openModal}
        title={i18next.t('participation')}
        renderSeparatorBefore
      >
        <ParticipationSectionSummary participationInfo={participationInfo} />
      </PhaseSection>
      <ParticipationModal
        participationInfo={participationInfo}
        closeModal={closeModal}
        open={modalOpen}
        updateCredit={updateCredit}
      />
    </>
  );
};
